import { buildQueryString } from '@/shared/lib/api/buildQuery';
import { clearObject } from '@/shared/lib/dataUtils/clearObject';

import { adaptRecentReviews } from '../adapters/adaptRecentReviews';
import { doctorRecommendationAdapter } from '../adapters/doctor';
import { doctorsListAdapter } from '../adapters/search';

import type { IResponseDataList, IPagination, IStandardResponse } from '@/entities/common/types/api';
import type { IRecommendationsDoctor } from '@/entities/common/types/recommendations';
import type { IDoctor } from '@/entities/doctor/model/types';
import type { IRecentReview } from '@/entities/review/model/types';
import type { Skill, SkillDetails } from '@/entities/skill/model/types';

import type { HttpClient } from '../http';

interface BaseSearchDoctorsQuery {
  withDiscount?: boolean;
  withHighRating?: boolean;
  withOnlineBooking?: boolean;
  hasChild?: boolean;
  appointmentType?: 'online' | 'offline' | 'ambulatory';
  worksToday?: boolean;
  worksNextDays?: boolean;
  worksComingWeekends?: boolean;
}

interface SearchDoctorsQuery extends BaseSearchDoctorsQuery {
  page: number;
}

type AppointmentTypeResponse = 'child' | 'ambulatory' | 'online' | 'offline';
type WorksForResponse = 'today' | 'nextThreeDays' | 'comingWeekends';
type SearchQueryAdapter = (SearchDoctorsQuery | BaseSearchDoctorsQuery) & { skillId?: number };

interface SearchDoctorsResponseQuery {
  'page': number;
  'withDiscount'?: 1;
  'withHighRating'?: 1;
  'withOnlineBooking'?: 1;
  'skillId'?: number;
  'appointmentType[]': AppointmentTypeResponse[];
  'worksFor': WorksForResponse[];
}

class SearchRepository {
  private http: HttpClient;
  private static skillsCache: Map<string, Skill[]> = new Map();

  private static readonly WORKS_MAPPING: Record<
    keyof Pick<SearchDoctorsQuery, 'worksToday' | 'worksNextDays' | 'worksComingWeekends'>,
    WorksForResponse
  > = {
    worksToday: 'today',
    worksNextDays: 'nextThreeDays',
    worksComingWeekends: 'comingWeekends',
  };

  private static readonly BOOLEAN_MAPPING: Record<
    keyof Pick<SearchDoctorsQuery, 'withDiscount' | 'withHighRating' | 'withOnlineBooking'>,
    keyof Pick<SearchDoctorsResponseQuery, 'withDiscount' | 'withHighRating' | 'withOnlineBooking'>
  > = {
    withDiscount: 'withDiscount',
    withHighRating: 'withHighRating',
    withOnlineBooking: 'withOnlineBooking',
  };

  constructor(http: HttpClient) {
    this.http = http;
  }

  private queryParamsAdapter(query: SearchQueryAdapter): SearchDoctorsResponseQuery {
    const adaptedQuery: Partial<SearchDoctorsResponseQuery> = {};

    if ('page' in query) {
      adaptedQuery.page = query.page;
    }

    Object.entries(SearchRepository.BOOLEAN_MAPPING).forEach(([queryKey, responseKey]) => {
      if (query[queryKey as keyof BaseSearchDoctorsQuery]) {
        (adaptedQuery[responseKey as keyof SearchDoctorsResponseQuery] as 1) = 1;
      }
    });

    const appointmentTypes: AppointmentTypeResponse[] = [query.appointmentType, query.hasChild && 'child'].filter(
      Boolean,
    ) as AppointmentTypeResponse[];

    if (appointmentTypes.length) {
      adaptedQuery['appointmentType[]'] = appointmentTypes;
    }

    const worksFor = Object.entries(SearchRepository.WORKS_MAPPING)
      .filter(([key]) => query[key as keyof typeof SearchRepository.WORKS_MAPPING])
      .map(([, value]) => value);

    if (worksFor.length) {
      adaptedQuery.worksFor = worksFor;
    }

    if (query.skillId) {
      adaptedQuery.skillId = query.skillId;
    }

    return adaptedQuery as SearchDoctorsResponseQuery;
  }

  private catchReturn(currentPage: number) {
    const defaultPagination = {
      perPage: 0,
      currentPage,
      hasNextPage: false,
      total: 0,
    };

    return {
      data: [],
      counters: {
        doctors: 0,
        medcenters: 0,
      },
      pagination: defaultPagination,
    };
  }

  // @todo: заменить на main
  public async getAllSkills(): Promise<Skill[]> {
    const cacheKey = 'all-skills';

    if (SearchRepository.skillsCache.has(cacheKey)) {
      return SearchRepository.skillsCache.get(cacheKey)!;
    }

    try {
      const response = await this.http.get<IResponseDataList<Skill>>('/references/skills');
      const skills = response.data;

      SearchRepository.skillsCache.set(cacheKey, skills);
      return skills;
    } catch (error) {
      console.error('Error fetching skills:', error);
      return [];
    }
  }

  private async searchDoctorRequest(query: SearchDoctorsResponseQuery) {
    try {
      const responseQuery = buildQueryString(query);
      const response = await this.http.get<IResponseDataList<IDoctor>>(`/doctors?${responseQuery}`);

      return {
        data: doctorsListAdapter(response.data),
        pagination: response.pagination,
      };
    } catch (error) {
      console.log('error >>>', error);

      return this.catchReturn(query.page);
    }
  }

  public async getDoctorsList(query: SearchDoctorsQuery): Promise<{
    data: IDoctor[];
    pagination: IPagination;
  }> {
    try {
      const responseQueryAdapted = this.queryParamsAdapter(query);

      return this.searchDoctorRequest(responseQueryAdapted);
    } catch (error) {
      console.log('error >>>', error);

      return this.catchReturn(query.page);
    }
  }

  public async getDoctorsListBySkill(
    skillId: number,
    query: SearchDoctorsQuery,
  ): Promise<{
    data: IDoctor[];
    pagination: IPagination;
  }> {
    try {
      const responseQueryAdapted = this.queryParamsAdapter({ ...query, skillId });

      return this.searchDoctorRequest(responseQueryAdapted);
    } catch (error) {
      console.log('error >>>', error);

      return this.catchReturn(query.page);
    }
  }

  public async getSkillData(skill: string): Promise<SkillDetails | null> {
    try {
      const response = await this.http.get<IStandardResponse<SkillDetails>>(`/skills/${skill}`);

      return response.data;
    } catch (error) {
      console.log('error >>>', error);

      return null;
    }
  }

  public async getRecentReviews(url: string): Promise<IRecentReview[]> {
    try {
      const response = await this.http.get<IStandardResponse<IRecentReview[]>>('/actual-comments', undefined, {
        url,
      });

      return adaptRecentReviews(response.data);
    } catch (error) {
      console.log('error >>>', error);
      return [];
    }
  }

  public async getTopDoctors(query: SearchDoctorsQuery, skillId?: string): Promise<IRecommendationsDoctor['list']> {
    try {
      const adaptedQuery = this.queryParamsAdapter(clearObject(query, ['page']));

      const data = await this.http.get<IStandardResponse<IRecommendationsDoctor>>(
        `/doctors/recommendations`,
        {},
        {
          ...adaptedQuery,
          type: 'tops',
          skillId: skillId ? [skillId] : null,
        },
      );

      return doctorRecommendationAdapter(data.data).list;
    } catch (error) {
      console.error('Error fetching recommendations:', error);

      return [];
    }
  }
}

export { SearchRepository };
export type { SearchDoctorsQuery };
